import { Product, ProductSlug } from "@/api/product-api";
import type { PolicyBySlug } from "@/libs/coupon/issued-coupon";
import { useSellableGames } from "@/hooks/useBhapticsContents";
import { useCurrency } from "@/hooks/useCurrency";
import { ProductQuery } from "@/services/product/ProductQuery";
import { useLocale } from "@/store/locale-store";
import { ProductUtil } from "@/utils/ProductUtil";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { usePricedFilter } from "@/hooks/usePricedFilter";
import { getBundleUpgradeCandidates } from "@/libs/product/bundle-preselect";

export function useProducts() {
  const { countryCode } = useLocale();
  return useQuery(ProductQuery.products(countryCode === "US" ? "US" : "KR"));
}

// 제품 ids를 받아서 추천제품을 반환하는 훅입니다.
// 카트 패널에서 추천제품을 보여주기 위해 사용합니다.
// 제품 ids가 비어있는 경우에는 모든 제품중에서 유효제품을 반환합니다.
// useCartWithProducts 가 카트 패널에서 사용되어서 ids 와 products 를 파라미터로 사용해도 됩니다.
export function useRecommendedProducts(ids: string[]) {
  const { data: allProducts = [] } = useProducts();
  const priced = usePricedFilter();

  const filterProducts = useCallback(
    (products: Product[]) =>
      priced(
        products
          .filter((product) => product.isActive)
          .filter((product) => !ProductUtil.isProductSoldOut(product)),
      ),
    [priced],
  );

  return useMemo(() => {
    const candidates = filterProducts(allProducts);
    // ids 가 비어있는 경우에는 모든 제품중에서 유효제품을 반환합니다.
    if (ids.length === 0) {
      return candidates;
    }

    const products = allProducts.filter((product) => ids.includes(product.id));
    const productSlugsSet = new Set(products.map((product) => product.slug));
    const recommendedSlugsSet = new Set(
      products.flatMap((product) => product.recommendedProducts),
    );
    const neededSlugs = Array.from(recommendedSlugsSet).filter(
      (slug) => !productSlugsSet.has(slug as ProductSlug),
    );
    const recommendedProducts =
      neededSlugs.length > 0
        ? allProducts.filter((product) => neededSlugs.includes(product.slug))
        : [];
    return filterProducts(recommendedProducts);
  }, [allProducts, ids]);
}

export function useProductBySlug(slug: ProductSlug, enabled = true) {
  const { countryCode } = useLocale();
  return useQuery({
    ...ProductQuery.productBySlug(slug, countryCode === "US" ? "US" : "KR"),
    enabled: !!slug && enabled,
  });
}

// 번들 제품의 유효한 제품들을 반환하는 훅입니다.
// 게임번들의 경우 옵션에 따라 보이지 않을수 있습니다.
export function useGetBundleProducts(product: Product) {
  const { data: products = [] } = useProducts();
  const sellableGames = useSellableGames({ inStockOnly: true });
  const currency = useCurrency();

  const { countryCode } = useLocale();

  // 규칙은 `getBundleUpgradeCandidates` 한 곳 — 서버 SEO·GMC 피드도 같은 사슬을 지난다(#5858).
  const bundles = useMemo(
    () =>
      getBundleUpgradeCandidates({
        products,
        product,
        // 번들 전용 게임 키 포함 목록(#6053)
        gameResells: sellableGames.bundleGameResells,
        currency,
        countryCode,
      }),
    [products, product, sellableGames.bundleGameResells, currency, countryCode],
  );

  return bundles;
}

export function useGetBundleProductComponents(product: Product) {
  const { data: products = [] } = useProducts();
  const components = ProductUtil.getBundleComponents(products, product);
  return components;
}

export function useGameBundles() {
  const { data: products = [], isLoading } = useProducts();

  const gameBundles = useMemo(() => {
    return ProductUtil.getGameBundles(products);
  }, [products]);

  return {
    data: gameBundles,
    isLoading,
  };
}

/**
 * slug → 쿠폰 적용 정책 맵 (표시·미리계산용).
 *
 * 카탈로그 응답의 `couponPolicy` 는 **mgmt 가 파생을 끝낸 값**이다(`AUTO` 가 오지 않는다). 여기서
 * `productType` 으로 다시 판정하지 않는다 — 같은 규칙이 두 곳에 있으면 어긋나는 순간 화면과 결제
 * 금액이 달라진다.
 *
 * 필드가 없는(mgmt 미배포) 응답에서는 그 slug 가 맵에 빠지고, 소비처가 `STACK`(종전 동작)으로 본다.
 * **권위는 서버**다 — 실제 적용은 `apply-coupon` 라우트가 카탈로그를 다시 읽고 draft 생성 시 mgmt 가
 * 라인별로 강제한다.
 */
export function useCouponPolicyBySlug(): PolicyBySlug {
  const { data: products = [] } = useProducts();
  return useMemo(() => {
    const map: PolicyBySlug = {};
    products.forEach((p) => {
      if (p.couponPolicy) map[p.slug] = p.couponPolicy;
    });
    return map;
  }, [products]);
}
